"use client";

import { useState, useEffect } from 'react';

interface FloatingElement {
  id: string;
  x: number;
  y: number;
  size: number;
  delay: number;
  duration: number;
}

const floatingIcons: FloatingElement[] = [
  { id: 'icon-1', x: 7,  y: 18, size: 42, delay: 0,   duration: 9   },
  { id: 'icon-2', x: 87, y: 14, size: 36, delay: 1.4, duration: 11  },
  { id: 'icon-3', x: 14, y: 68, size: 38, delay: 2.2, duration: 8.5 },
  { id: 'icon-4', x: 82, y: 64, size: 44, delay: 0.7, duration: 10  },
];

const dataPoints: FloatingElement[] = [
  { id: 'dp-1', x: 22, y: 35, size: 8,  delay: 0,   duration: 3.4 },
  { id: 'dp-2', x: 76, y: 28, size: 7,  delay: 0.8, duration: 3.9 },
  { id: 'dp-3', x: 38, y: 62, size: 9,  delay: 1.4, duration: 4.2 },
  { id: 'dp-4', x: 64, y: 55, size: 7,  delay: 2.0, duration: 3.6 },
];

function TowerIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 50 50" fill="none">
      <line x1="25" y1="6" x2="25" y2="44" stroke="#C5A572" strokeWidth="2" strokeOpacity="0.5" />
      <line x1="14" y1="18" x2="36" y2="18" stroke="#C5A572" strokeWidth="1.5" strokeOpacity="0.4" />
      <line x1="10" y1="28" x2="40" y2="28" stroke="#C5A572" strokeWidth="1.5" strokeOpacity="0.4" />
      <line x1="6" y1="38" x2="44" y2="38" stroke="#C5A572" strokeWidth="1.5" strokeOpacity="0.4" />
      <line x1="14" y1="18" x2="25" y2="6" stroke="#C5A572" strokeWidth="1.5" strokeOpacity="0.35" />
      <line x1="36" y1="18" x2="25" y2="6" stroke="#C5A572" strokeWidth="1.5" strokeOpacity="0.35" />
      <circle cx="25" cy="6" r="3" fill="#C5A572" opacity="0.4" />
    </svg>
  );
}

function WifiIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 50 50" fill="none">
      <path d="M8 22 Q25 8 42 22" stroke="#C5A572" strokeWidth="2" fill="none" strokeOpacity="0.3" strokeLinecap="round" />
      <path d="M13 28 Q25 18 37 28" stroke="#C5A572" strokeWidth="2" fill="none" strokeOpacity="0.45" strokeLinecap="round" />
      <path d="M18 34 Q25 28 32 34" stroke="#C5A572" strokeWidth="2" fill="none" strokeOpacity="0.6" strokeLinecap="round" />
      <circle cx="25" cy="40" r="3" fill="#C5A572" opacity="0.5" />
    </svg>
  );
}

function NetworkNodeIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 50 50" fill="none">
      <circle cx="25" cy="25" r="5" fill="#C5A572" opacity="0.35" stroke="#C5A572" strokeWidth="1.5" strokeOpacity="0.5" />
      <circle cx="10" cy="12" r="4" fill="#C5A572" opacity="0.2" stroke="#C5A572" strokeWidth="1" strokeOpacity="0.4" />
      <circle cx="40" cy="12" r="4" fill="#C5A572" opacity="0.2" stroke="#C5A572" strokeWidth="1" strokeOpacity="0.4" />
      <circle cx="10" cy="38" r="4" fill="#C5A572" opacity="0.2" stroke="#C5A572" strokeWidth="1" strokeOpacity="0.4" />
      <circle cx="40" cy="38" r="4" fill="#C5A572" opacity="0.2" stroke="#C5A572" strokeWidth="1" strokeOpacity="0.4" />
      <line x1="25" y1="25" x2="10" y2="12" stroke="#C5A572" strokeWidth="1" strokeOpacity="0.3" />
      <line x1="25" y1="25" x2="40" y2="12" stroke="#C5A572" strokeWidth="1" strokeOpacity="0.3" />
      <line x1="25" y1="25" x2="10" y2="38" stroke="#C5A572" strokeWidth="1" strokeOpacity="0.3" />
      <line x1="25" y1="25" x2="40" y2="38" stroke="#C5A572" strokeWidth="1" strokeOpacity="0.3" />
    </svg>
  );
}

const iconComponents = [TowerIcon, WifiIcon, NetworkNodeIcon, TowerIcon];

export default function TelecomHeroSection() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => { setIsHydrated(true); }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 12,
        y: (e.clientY / window.innerHeight - 0.5) * 12,
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section className="relative bg-navy pt-24 pb-14 px-4 min-h-[100svh] sm:pt-28 sm:pb-16 sm:px-6 md:pt-32 md:pb-20 md:min-h-screen flex items-center overflow-hidden">
      {/* Animated Background Gradients */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy to-[#0e1c2e]"></div>
        <div className="absolute top-1/4 right-1/4 w-[600px] h-[600px] bg-gold/5 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-1/4 left-1/5 w-[500px] h-[500px] bg-gold/8 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1.5s' }}></div>
        <div className="absolute top-1/2 left-1/2 w-[300px] h-[300px] bg-gold/4 rounded-full blur-2xl animate-pulse-slow" style={{ animationDelay: '0.8s' }}></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full min-w-0 max-w-5xl mx-auto text-center">
        {/* Minimal compliance motif — single horizontal rule + shield mark */}
        <div
          className="mb-8 sm:mb-10 flex items-center justify-center gap-4 sm:gap-5 max-w-md mx-auto"
          aria-hidden="true"
        >
          <span className="h-px flex-1 min-w-[2.5rem] bg-gradient-to-r from-transparent via-gold/25 to-gold/45" />
          <svg
            className="shrink-0 text-gold/75 w-7 h-7 sm:w-8 sm:h-8"
            viewBox="0 0 32 32"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.15"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M16 5.5l8.5 3.5v7.2c0 5.2-3.6 9.9-8.5 11.8-4.9-1.9-8.5-6.6-8.5-11.8V9L16 5.5z" />
            <path d="M11.5 15.5l3.2 3.2 6.3-6.3" />
          </svg>
          <span className="h-px flex-1 min-w-[2.5rem] bg-gradient-to-l from-transparent via-gold/25 to-gold/45" />
        </div>

        <h1 className="font-serif text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight sm:leading-[1.15] font-bold mb-8 animate-fade-in">
          Telecom Network Data Chaos Solved with Real-Time{' '}
          <span
            className="italic"
            style={{
              color: '#C5A572',
              textShadow: '0 0 20px rgba(197, 165, 114, 0.4), 0 0 40px rgba(197, 165, 114, 0.2)',
            }}
          >
            Subscriber Intelligence
          </span>
        </h1>
        <p className="text-white/90 text-base sm:text-lg md:text-xl leading-relaxed max-w-4xl mx-auto mb-12 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          Transform fragmented OSS, BSS, network monitoring, and billing systems into unified telecom intelligence and operational visibility.
        </p>
        <button
          className="w-full max-w-sm sm:w-auto mx-auto sm:mx-0 bg-gold hover:bg-gold/90 text-navy font-bold text-base sm:text-lg px-6 sm:px-10 py-3.5 sm:py-4 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 animate-fade-in"
          style={{ animationDelay: '0.4s' }}
        >
          Schedule Telecom Data Assessment →
        </button>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          25% { transform: translateY(-18px) translateX(8px); }
          50% { transform: translateY(-10px) translateX(-8px); }
          75% { transform: translateY(-14px) translateX(4px); }
        }
        @keyframes telecom-flow {
          0% { stroke-dashoffset: 0; }
          100% { stroke-dashoffset: 800; }
        }
        .animate-telecom-flow {
          stroke-dasharray: 14 6;
          animation: telecom-flow 4.5s linear infinite;
        }
        .animate-pulse-slow {
          animation: pulse 4.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </section>
  );
}
